import React from 'react';
import { cn } from '@/utils/helpers';

// 头部属性接口
export interface HeaderProps {
  title?: string;
  subtitle?: string;
  logo?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  sticky?: boolean;
  transparent?: boolean;
  children?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({
  title = '充值调试',
  subtitle = '',
  logo,
  actions,
  className,
  sticky = false,
  transparent = false,
  children
}) => {
  return (
    <header
      className={cn(
        // 基础样式 - Fluent Design
        'w-full transition-all duration-200',
        // 背景样式 - 亚克力材质
        transparent
          ? 'bg-transparent'
          : 'acrylic border-b border-white/20',
        // 粘性定位
        sticky && 'sticky top-0 z-50',
        // 阴影效果 - Fluent Design 阴影
        !transparent && 'shadow-lg',
        // 自定义类名
        className
      )}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* 左侧：Logo 和标题 */}
          <div className="flex items-center space-x-3">
            {logo && (
              <div className="flex-shrink-0">
                {logo}
              </div>
            )}
            
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold text-white truncate">
                {title}
              </h1>
              {subtitle && (
                <p className="text-xs sm:text-sm text-white/70 truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* 右侧：操作按钮 */}
          {actions && (
            <div className="flex-shrink-0 ml-4 animate-fade-scale">
              {actions}
            </div>
          )}
        </div>

        {/* 自定义内容 */}
        {children && (
          <div className="mt-6 animate-slide-up">
            {children}
          </div>
        )}
      </div>
    </header>
  );
};

// 简单头部组件
export interface SimpleHeaderProps {
  title: string;
  onBack?: () => void;
  actions?: React.ReactNode;
  className?: string;
}

export const SimpleHeader: React.FC<SimpleHeaderProps> = ({
  title,
  onBack,
  actions,
  className
}) => {
  return (
    <header
      className={cn(
        'bg-white border-b border-gray-200 px-4 py-3',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
              aria-label="返回"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
          
          <h1 className="text-lg font-semibold text-gray-900 truncate">
            {title}
          </h1>
        </div>

        {actions && (
          <div className="flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
};

// 移动端头部组件
export interface MobileHeaderProps {
  title: string;
  leftAction?: React.ReactNode;
  rightAction?: React.ReactNode;
  className?: string;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  leftAction,
  rightAction,
  className
}) => {
  return (
    <header
      className={cn(
        'bg-white border-b border-gray-200 px-4 py-3 safe-area-inset',
        className
      )}
    >
      <div className="flex items-center justify-between h-11">
        {/* 左侧操作 */}
        <div className="flex items-center justify-start w-16">
          {leftAction}
        </div>

        {/* 标题 */}
        <div className="flex-1 text-center">
          <h1 className="text-lg font-semibold text-gray-900 truncate px-4">
            {title}
          </h1>
        </div>

        {/* 右侧操作 */}
        <div className="flex items-center justify-end w-16">
          {rightAction}
        </div>
      </div>
    </header>
  );
};

// 状态头部组件
export interface StatusHeaderProps extends HeaderProps {
  status?: 'loading' | 'error' | 'success' | 'idle';
  statusMessage?: string;
}

export const StatusHeader: React.FC<StatusHeaderProps> = ({
  status = 'idle',
  statusMessage,
  children,
  ...props
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-600';
      case 'error':
        return 'text-red-600';
      case 'success':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return (
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <Header {...props}>
      {(statusMessage || status !== 'idle') && (
        <div className="flex items-center space-x-2 mt-2">
          <span className={cn('flex items-center space-x-1', getStatusColor())}>
            {getStatusIcon()}
            {statusMessage && (
              <span className="text-sm font-medium">
                {statusMessage}
              </span>
            )}
          </span>
        </div>
      )}
      {children}
    </Header>
  );
};

export default Header;